import { IsInt, IsNotEmpty, IsOptional, ValidateNested } from '@pristine-ts/class-validator';
import { FieldTypeEnum } from '../../../../../enums/field-type.enum';
import {
  advancedFormGroup,
  advancedFormControl,
  FormControlTypeEnum,
} from '@magieno/common';
import { translationBaseKey } from '../../../../../decorators/translation-base-key.decorator';
import { ErrorMessageBuilder } from '../../../../../builders/error-message.builder';
import { type } from '@pristine-ts/data-mapping-common';
import {
  IncomeTaxPersonalSpouseFederalInSalaryDividendOptions,
} from './income-tax-personal-spouse-federal-in-salary-dividend.options';
import {
  IncomeTaxPersonalSpouseOptions,
} from '../../../income-tax/personal/personal/income-tax-personal-spouse.options';
import { CalculationDate } from '../../../../../models/calculation.date';
import { clonePropertyMetadata } from '@pristine-ts/metadata';
import {
  IncomeTaxPersonalSpouseCommonOptions,
} from '../../../income-tax/personal/common/income-tax-personal-spouse-common.options';
import { v4 as uuid } from 'uuid';

@translationBaseKey('income-tax-personal-spouse-in-salary-dividend-options')
export class IncomeTaxPersonalSpouseInSalaryDividendOptions implements Omit<IncomeTaxPersonalSpouseOptions,
  'federal' |
  'businessIncome' |
  'employmentIncomeForEI'
> {
  @type(() => new IncomeTaxPersonalSpouseFederalInSalaryDividendOptions())
  @advancedFormGroup({
    classType: IncomeTaxPersonalSpouseFederalInSalaryDividendOptions,
  })
  @ValidateNested()
  federal: IncomeTaxPersonalSpouseFederalInSalaryDividendOptions;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Gain en capital (perte en capital en négatif)',
        en: 'Negative capital gain or loss',
      },
      tooltip: {
        fr: 'Indiquez le montant du gain en capital ou de la perte en capital (négatif) sur la disposition d\'actions côtés en bourse, de biens amortissables, d\'obligations et autres. Ces montants peuvent avoir un impact sur les impôts.',
        en: 'Specify the amount of negative capital gain or loss on the disposition of publicly traded stocks, depreciable property, bonds, and similar items. These amounts may have tax implications.',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  gainPubliclyTradeShares: number;

  @clonePropertyMetadata<IncomeTaxPersonalSpouseCommonOptions>(IncomeTaxPersonalSpouseCommonOptions, 'id')
  id: string = uuid();

  @clonePropertyMetadata<IncomeTaxPersonalSpouseCommonOptions>(IncomeTaxPersonalSpouseCommonOptions, 'firstName')
  firstName: string;

  @clonePropertyMetadata<IncomeTaxPersonalSpouseCommonOptions>(IncomeTaxPersonalSpouseCommonOptions, 'lastName')
  lastName: string;

  @clonePropertyMetadata<IncomeTaxPersonalSpouseCommonOptions>(IncomeTaxPersonalSpouseCommonOptions, 'birthdate')
  birthdate: CalculationDate;

  @clonePropertyMetadata<IncomeTaxPersonalSpouseCommonOptions>(IncomeTaxPersonalSpouseCommonOptions, 'employmentIncome')
  employmentIncome: number;

  @clonePropertyMetadata<IncomeTaxPersonalSpouseCommonOptions>(IncomeTaxPersonalSpouseCommonOptions, 'realEligibleDividend')
  realEligibleDividend: number;

  @clonePropertyMetadata<IncomeTaxPersonalSpouseCommonOptions>(IncomeTaxPersonalSpouseCommonOptions, 'realNonEligibleDividend')
  realNonEligibleDividend: number;

  @clonePropertyMetadata<IncomeTaxPersonalSpouseCommonOptions>(IncomeTaxPersonalSpouseCommonOptions, 'othersIncomesLine13000')
  othersIncomesLine13000: number;

  @clonePropertyMetadata<IncomeTaxPersonalSpouseCommonOptions>(IncomeTaxPersonalSpouseCommonOptions, 'isEligibleToCWB')
  isEligibleToCWB: boolean;

  @clonePropertyMetadata<IncomeTaxPersonalSpouseCommonOptions>(IncomeTaxPersonalSpouseCommonOptions, 'isEmployeeSubjectToEI')
  isEmployeeSubjectToEI: boolean;
}

